import React, { memo, useCallback, useRef } from 'react';

import { Box, FormControl, FormHelperText, IconButton, Typography } from '@mui/material';
import { CancelIcon, EditPenIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import { FormTestIds } from '../form.testIds';

import {
  clearButtonStyles,
  formControlStyles,
  helperTextClasses,
  hiddenInputStyles,
  inputContainerStyles,
  inputControlsWrapperStyles,
  labelStyles,
  noWrapLabelStyles,
  placeholderButtonStyles,
} from './ColorPicker.styles';

const ColorPicker = ({
  value = '',
  variant = '',
  label = '',
  name,
  error = false,
  helperText = '',
  withStaticHelper = false,
  className,
  disabled = false,
  dataTestId,
  onClear,
  onPickerOpen,
  showPlaceholderIcon = false,
  noWrapLabel = false,
  ...props
}) => {
  const inputRef = useRef();
  const isPlaceholder = showPlaceholderIcon && !onClear;

  const onPlaceholderClick = useCallback(() => {
    onPickerOpen?.();
    inputRef.current?.click();
  }, [onPickerOpen]);

  return (
    <FormControl className={className} error={error} sx={formControlStyles} variant={variant}>
      <Box data-testid={dataTestId || FormTestIds.getColorInputId(name)} sx={inputContainerStyles}>
        {label && (
          <Typography sx={noWrapLabel ? noWrapLabelStyles : labelStyles} variant="regular14">
            {label}:
          </Typography>
        )}
        <Box sx={inputControlsWrapperStyles}>
          {isPlaceholder && (
            <IconButton
              data-testid={FormTestIds.COLOR_PICKER_PLACEHOLDER_BUTTON}
              disabled={disabled}
              size="small"
              sx={placeholderButtonStyles}
              onClick={onPlaceholderClick}
            >
              <EditPenIcon size={IconSize[20]} />
            </IconButton>
          )}
          <input
            {...props}
            disabled={disabled}
            id={name}
            name={name}
            ref={inputRef}
            style={isPlaceholder ? hiddenInputStyles : null}
            type="color"
            value={value}
          />
          {onClear && (
            <IconButton
              data-testid={FormTestIds.COLOR_PICKER_CLEAR_BUTTON}
              size="small"
              sx={clearButtonStyles}
              onClick={onClear}
            >
              <CancelIcon size={IconSize[16]} />
            </IconButton>
          )}
        </Box>
      </Box>
      <FormHelperText classes={withStaticHelper ? null : helperTextClasses}>
        {helperText}
      </FormHelperText>
    </FormControl>
  );
};

ColorPicker.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  variant: PropTypes.string,
  label: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  withStaticHelper: PropTypes.bool,
  disabled: PropTypes.bool,
  dataTestId: PropTypes.string,
  showPlaceholderIcon: PropTypes.bool,
  noWrapLabel: PropTypes.bool,
  onClear: PropTypes.func,
  onPickerOpen: PropTypes.func,
};

export default memo(ColorPicker);
